/**
 * RWBR — ONE LEAGUE ON THE SHELF AT A TIME. D-EZ-99 (a).
 *
 * The audit found that starting a new league, or loading one with an empty
 * archive, kept the previous league's settled seasons on the shelf — so two
 * leagues' seasons folded into one set of career totals. These two pure rules
 * are what the shell now does at each of those moments, and they are tested.
 */

import type { CareerSeason } from './careerRunner.ts';

export interface Shelf {
  leagueId: string;
  /** The latest settled season. */
  career: CareerSeason | null;
  /** Earlier settled seasons, oldest first. */
  history: CareerSeason[];
}

/** A brand-new league: an empty shelf under a new id. Nothing carries over. */
export function freshShelf(leagueId: string): Shelf {
  return { leagueId, career: null, history: [] };
}

/** A league that came back (account, device or file): its seasons and nothing else. */
export function shelfFromArchive(leagueId: string, archive: readonly CareerSeason[]): Shelf {
  const byNumber = new Map<number, CareerSeason>();
  for (const s of archive) byNumber.set(s.seasonNumber, s);
  const sorted = [...byNumber.values()].sort((a, b) => a.seasonNumber - b.seasonNumber);
  return { leagueId, career: sorted[sorted.length - 1] ?? null, history: sorted.slice(0, -1) };
}

/**
 * A SEASON JUST SETTLED. It goes on top of its own league's shelf. If it belongs
 * to a different league than the shelf holds, it is REFUSED as a fold — it starts
 * its own shelf instead — so two leagues can never merge.
 */
export function foldSettled(shelf: Shelf, settled: CareerSeason, settledLeagueId: string | undefined): Shelf {
  if (settledLeagueId && settledLeagueId !== shelf.leagueId) {
    return { leagueId: settledLeagueId, career: settled, history: [] };
  }
  const history = shelf.career ? [...shelf.history, shelf.career] : [...shelf.history];
  return {
    leagueId: shelf.leagueId,
    career: settled,
    // A season number already on the shelf is replaced, never duplicated.
    history: history.filter((s) => s.seasonNumber !== settled.seasonNumber),
  };
}
